import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import './SellListing.css';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM',
  'NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA',
  'WV','WI','WY','DC'
];

const STAGES = [
  { value: '1', label: 'Stage 1 — Stock',   desc: 'No modifications, completely stock' },
  { value: '2', label: 'Stage 2 — Basic',   desc: 'Simple mods like pegs or seat cover' },
  { value: '3', label: 'Stage 3 — Dialed',  desc: 'Everything except powertrain & suspension' },
  { value: '4', label: 'Stage 4 — Built',   desc: 'Cosmetic + some powertrain or suspension upgrades' },
  { value: '5', label: 'Stage 5 — Tank',    desc: 'Every part upgraded, zero stock parts remaining' },
];

const MAX_IMAGES = 8;

export default function SellListing() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const { id }    = useParams();
  const isEdit    = Boolean(id);

  const [form, setForm] = useState({
    title:'', description:'', price:'',
    category:'street_legal', condition:'good', stage:'1',
    brand:'', model:'', year:'', mileage:'',
    city:'', state:'', zip_code:'',
  });
  const [images,   setImages]   = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [fetchingEdit, setFetchingEdit] = useState(isEdit);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (isEdit) fetchListing();
  }, [user, isEdit]);

  async function fetchListing() {
    const { data } = await supabase.from('listings').select('*').eq('id', id).single();
    if (!data || data.seller_id !== user?.id) { navigate('/my-listings'); return; }
    setForm({
      title: data.title||'', description: data.description||'',
      price: data.price||'', category: data.category||'street_legal',
      condition: data.condition||'good', stage: String(data.stage||'1'),
      brand: data.brand||'', model: data.model||'', year: data.year||'', mileage: data.mileage||'',
      city: data.city||'', state: data.state||'', zip_code: data.zip_code||'',
    });
    setImages(data.images||[]);
    setFetchingEdit(false);
  }

  const upd = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const selectedStage = STAGES.find(s => s.value === form.stage);

  const handleFiles = e => {
    const files = Array.from(e.target.files);
    const rem   = MAX_IMAGES - images.length - newFiles.length;
    setNewFiles(prev => [...prev, ...files.slice(0, rem)]);
  };

  async function uploadImages() {
    const urls = [];
    for (const file of newFiles) {
      const ext  = file.name.split('.').pop();
      const path = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: upErr } = await supabase.storage.from('listing-images').upload(path, file);
      if (upErr) throw upErr;
      const { data } = supabase.storage.from('listing-images').getPublicUrl(path);
      urls.push(data.publicUrl);
    }
    return urls;
  }

async function handleSubmit(e) {
  e.preventDefault();
  setError(''); setLoading(true);
  try {
    const uploaded  = await uploadImages();
    const allImages = [...images, ...uploaded];
    const locationStr = [form.city, form.state, form.zip_code].filter(Boolean).join(', ');

    const payload = {
      ...form,
      price:    parseFloat(form.price),
      year:     form.year    ? parseInt(form.year)    : null,
      mileage:  form.mileage ? parseInt(form.mileage) : null,
      stage:    parseInt(form.stage),
      location: locationStr,
      images:   allImages,
      seller_id: user.id,
      updated_at: new Date().toISOString(),
    };

    if (isEdit) {
      const { error: e } = await supabase.from('listings').update(payload).eq('id', id);
      if (e) throw e;
      navigate(`/listings/${id}`);
    } else {
      const { data, error: e } = await supabase.from('listings').insert(payload).select().single();
      if (e) throw e;
      navigate(`/listings/${data.id}`);
    }
  } catch (err) {
    setError(err.message || 'Something went wrong.');
  }
  setLoading(false);
}

  if (fetchingEdit) return <div className="spinner" />;
  const totalImages = images.length + newFiles.length;

  return (
    <div className="sell-page page-enter">
      <div className="container">
        <div className="sell-header">
          <h1>{isEdit ? 'Edit Listing' : 'New Listing'}</h1>
          <p>{isEdit ? 'Update your listing details.' : 'List your e-moto and reach buyers across the country.'}</p>
        </div>

        <div className="sell-layout">
          <form className="sell-form" onSubmit={handleSubmit}>
            {error && <div className="alert alert-error">{error}</div>}

            <div className="form-section">
              <h3 className="fsec-title">Basic Info</h3>
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input name="title" className="form-input" placeholder="e.g. 2023 Sur-Ron Ultra Bee" value={form.title} onChange={upd} required />
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select name="category" className="form-select" value={form.category} onChange={upd} required>
                    <option value="street_legal">Street Legal</option>
                    <option value="mini_moto">Mini Moto</option>
                    <option value="medium_moto">Medium Moto</option>
                    <option value="mega_moto">Mega Moto</option>
                    <option value="part">Part / Component</option>
                    <option value="accessory">Accessory</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Condition *</label>
                  <select name="condition" className="form-select" value={form.condition} onChange={upd} required>
                    <option value="new">New</option>
                    <option value="like_new">Like New</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="parts_only">Parts Only</option>
                  </select>
                </div>
              </div>

              {/* Build Stage */}
              <div className="form-group">
                <label className="form-label">Build Stage *</label>
                <select name="stage" className="form-select" value={form.stage} onChange={upd} required>
                  {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                {selectedStage && (
                  <p className="stage-desc">{selectedStage.desc}</p>
                )}
              </div>

              <div className="stage-guide">
                <p className="fsec-title" style={{marginBottom:12}}>Stage Guide</p>
                {STAGES.map(s => (
                  <div key={s.value} className={`stage-row ${form.stage === s.value ? 'active' : ''}`}>
                    <span className="stage-num">{s.value}</span>
                    <div>
                      <span className="stage-name">{s.label.split('—')[1].trim()}</span>
                      <span className="stage-desc-small">{s.desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-group" style={{marginTop:18}}>
                <label className="form-label">Price (USD) *</label>
                <input name="price" type="number" min="0" step="0.01" className="form-input" placeholder="0.00" value={form.price} onChange={upd} required />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea name="description" className="form-textarea" placeholder="Describe condition, history, mods, battery health..." value={form.description} onChange={upd} />
              </div>
            </div>

            <div className="form-section">
              <h3 className="fsec-title">Specs</h3>
              <div className="form-row-2">
                <div className="form-group"><label className="form-label">Brand</label><input name="brand" className="form-input" placeholder="Sur-Ron, Stark, Cake..." value={form.brand} onChange={upd} /></div>
                <div className="form-group"><label className="form-label">Model</label><input name="model" className="form-input" placeholder="Ultra Bee, Varg..." value={form.model} onChange={upd} /></div>
              </div>
              <div className="form-row-2">
                <div className="form-group"><label className="form-label">Year</label><input name="year" type="number" className="form-input" placeholder="2023" min="2010" max="2030" value={form.year} onChange={upd} /></div>
                <div className="form-group"><label className="form-label">Mileage (mi)</label><input name="mileage" type="number" className="form-input" placeholder="0" min="0" value={form.mileage} onChange={upd} /></div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="fsec-title">Location</h3>
              <div className="form-row-3">
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">City</label>
                  <input name="city" className="form-input" placeholder="Los Angeles" value={form.city} onChange={upd} />
                </div>
                <div className="form-group" style={{flex:1}}>
                  <label className="form-label">State</label>
                  <select name="state" className="form-select" value={form.state} onChange={upd}>
                    <option value="">Select...</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{flex:1}}>
                  <label className="form-label">ZIP Code</label>
                  <input name="zip_code" className="form-input" placeholder="90210" maxLength={5} value={form.zip_code} onChange={upd} />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="fsec-title">Photos ({totalImages}/{MAX_IMAGES})</h3>
              <p className="fsec-hint">First image is the cover photo.</p>
              <div className="image-grid">
                {images.map((url, i) => (
                  <div key={`e-${i}`} className="img-thumb">
                    <img src={url} alt="" />
                    <button type="button" className="img-remove" onClick={() => setImages(p => p.filter((_,j) => j !== i))}>✕</button>
                    {i === 0 && <span className="img-cover">Cover</span>}
                  </div>
                ))}
                {newFiles.map((f, i) => (
                  <div key={`n-${i}`} className="img-thumb">
                    <img src={URL.createObjectURL(f)} alt="" />
                    <button type="button" className="img-remove" onClick={() => setNewFiles(p => p.filter((_,j) => j !== i))}>✕</button>
                    {images.length === 0 && i === 0 && <span className="img-cover">Cover</span>}
                  </div>
                ))}
                {totalImages < MAX_IMAGES && (
                  <label className="img-add">
                    <input type="file" accept="image/*" multiple onChange={handleFiles} hidden />
                    <span className="img-add-plus">+</span>
                    <span className="img-add-label">Photo</span>
                  </label>
                )}
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-lg sell-submit" disabled={loading}>
              {loading ? 'Saving...' : isEdit ? 'Update Listing' : 'Publish Listing'}
            </button>
          </form>

          <aside className="sell-tips">
            <h3 className="tips-title">Listing Tips</h3>
            <ul className="tips-list">
              <li>Clear photos from multiple angles sell faster</li>
              <li>Include battery health and charge cycle count</li>
              <li>List any mods, upgrades, or recent service</li>
              <li>Be honest about your build stage</li>
              <li>Add your city and ZIP so local buyers can find you</li>
              <li>Respond to messages within 24 hours</li>
            </ul>
          </aside>
        </div>
      </div>
    </div>
  );
}
